import { useEffect, useRef } from "react";
import { RINGS, appLinks, bezierPoint, clamp, easeOutCubic, linkControl, makeQuanta, ringPoint } from "../../lib/quantaOrbit";

// Hero motion: the three apps sit inside one orbital system. Rings draw themselves in, quanta
// ride the rings in perspective, and pulses travel between the apps. Everything is canvas 2D,
// with no dependencies. Anchors are the elements inside `containerRef` marked data-quanta-anchor.
// Reduced motion gets one finished still frame; the loop pauses off-screen and in hidden tabs.

const PALETTE = {
  violet: [131, 88, 255], // accent
  lilac: [185, 160, 255], // accent-lighter
  blue: [66, 138, 248], // blue
};
const rgba = ([r, g, b], a) => `rgba(${r},${g},${b},${a})`;

function glowSprite(rgb) {
  const c = document.createElement("canvas");
  c.width = c.height = 64;
  const g = c.getContext("2d");
  const grad = g.createRadialGradient(32, 32, 0, 32, 32, 32);
  grad.addColorStop(0, "rgba(255,255,255,1)");
  grad.addColorStop(0.18, rgba(rgb, 0.95));
  grad.addColorStop(0.45, rgba(rgb, 0.28));
  grad.addColorStop(1, rgba(rgb, 0));
  g.fillStyle = grad;
  g.fillRect(0, 0, 64, 64);
  return c;
}

export default function QuantaField({ containerRef, className = "" }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return undefined;
    const ctx = canvas.getContext("2d");
    if (!ctx) return undefined;

    const motionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const sprites = { violet: glowSprite(PALETTE.violet), blue: glowSprite(PALETTE.blue) };
    const quanta = makeQuanta(66);
    const pointer = { tx: 0, ty: 0, x: 0, y: 0 };
    let width = 0;
    let height = 0;
    let raf = 0;
    let visible = true;
    let startedAt = null;

    function resize() {
      const rect = canvas.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      width = rect.width;
      height = rect.height;
      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }

    // App icon centres in canvas coordinates (the icons float, so this is read every frame).
    function anchors() {
      const origin = canvas.getBoundingClientRect();
      return [...container.querySelectorAll("[data-quanta-anchor]")].map((el) => {
        const r = el.getBoundingClientRect();
        return { x: r.left + r.width / 2 - origin.left, y: r.top + r.height / 2 - origin.top };
      });
    }

    function draw(seconds) {
      const dark = document.documentElement.classList.contains("dark");
      const points = anchors();
      ctx.clearRect(0, 0, width, height);
      if (!points.length) return;

      // The system is centred on the stage (the apps sit around it), sized to fit inside it.
      const cx = width / 2;
      const cy = height / 2;
      const R = Math.min(width, height) * 0.42;
      const view = { cx, cy, R, tiltOffset: pointer.y * 0.16, yawOffset: pointer.x * 0.14 };

      const intro = easeOutCubic(seconds / 1.8);
      const linksIn = easeOutCubic((seconds - 0.8) / 1.2);
      const strength = 1;
      ctx.globalCompositeOperation = dark ? "lighter" : "source-over";

      // Nucleus: a slow breathing glow at the heart of the system.
      const breath = 0.85 + 0.15 * Math.sin(seconds * 1.1);
      const nucleus = ctx.createRadialGradient(cx, cy, 0, cx, cy, R * 0.95 * breath);
      nucleus.addColorStop(0, rgba(PALETTE.violet, (dark ? 0.32 : 0.18) * intro));
      nucleus.addColorStop(0.55, rgba(PALETTE.blue, (dark ? 0.1 : 0.06) * intro));
      nucleus.addColorStop(1, rgba(PALETTE.blue, 0));
      ctx.fillStyle = nucleus;
      ctx.fillRect(0, 0, width, height);

      // Rings, drawn in as the intro plays; the half behind the nucleus is fainter.
      ctx.lineWidth = 1.2;
      RINGS.forEach((ring, i) => {
        const turn = seconds * ring.spin * 0.25;
        const reveal = clamp(intro * 1.25 - i * 0.12);
        const steps = 140;
        let prev = null;
        for (let s = 0; s <= steps * reveal; s++) {
          const p = ringPoint(ring, turn + (s / steps) * Math.PI * 2, view);
          if (prev) {
            const front = p.z < 0;
            ctx.strokeStyle = rgba(front || !dark ? PALETTE.violet : PALETTE.lilac, (front ? 0.5 : dark ? 0.18 : 0.2) * strength);
            ctx.beginPath();
            ctx.moveTo(prev.x, prev.y);
            ctx.lineTo(p.x, p.y);
            ctx.stroke();
          }
          prev = p;
        }
      });

      // Links between the apps: a faint flowing dashed thread, plus travelling pulses.
      const centre = { x: cx, y: cy };
      ctx.lineWidth = 1;
      ctx.setLineDash([3, 7]);
      ctx.lineDashOffset = -seconds * 14;
      for (const [a, b] of appLinks(points)) {
        const c = linkControl(points[a], points[b], centre, 0.42);
        ctx.strokeStyle = rgba(PALETTE.violet, 0.32 * linksIn * strength);
        ctx.beginPath();
        ctx.moveTo(points[a].x, points[a].y);
        ctx.quadraticCurveTo(c.x, c.y, points[b].x, points[b].y);
        ctx.stroke();
      }
      ctx.setLineDash([]);
      appLinks(points).forEach(([a, b], li) => {
        const c = linkControl(points[a], points[b], centre, 0.42);
        for (let k = 0; k < 2; k++) {
          const forward = (li + k) % 2 === 0;
          const head = (seconds * 0.22 + k * 0.5 + li * 0.17) % 1;
          for (let tail = 0; tail < 9; tail++) {
            const t = clamp(head - tail * 0.012);
            const p = bezierPoint(forward ? points[a] : points[b], c, forward ? points[b] : points[a], t);
            // Fade in/out near the icons so pulses emerge from and sink into the apps.
            const edge = Math.min(t, 1 - t) * 6;
            const alpha = clamp(edge) * (1 - tail / 9) * linksIn;
            const size = (tail === 0 ? 14 : 9) * (1 - tail / 12);
            ctx.globalAlpha = alpha * strength;
            ctx.drawImage(sprites.blue, p.x - size / 2, p.y - size / 2, size, size);
          }
        }
      });
      ctx.globalAlpha = 1;

      // Quanta, back to front, sized and lit by depth.
      const projected = quanta.map((q) => ({
        q,
        p: ringPoint(RINGS[q.ring], q.phase + seconds * q.speed, view),
      }));
      projected.sort((m, n) => n.p.z - m.p.z);
      for (const { q, p } of projected) {
        const appear = clamp((intro - q.delay * 0.6) / 0.4);
        const depth = clamp((p.scale - 0.7) / 0.6);
        const twinkle = 0.75 + 0.25 * Math.sin(seconds * 2.3 + q.phase * 5);
        const size = q.size * p.scale * (6 + depth * 6);
        ctx.globalAlpha = appear * (0.35 + depth * 0.65) * twinkle * strength;
        ctx.drawImage(sprites[q.hue], p.x - size / 2, p.y - size / 2, size, size);
      }
      ctx.globalAlpha = 1;
      ctx.globalCompositeOperation = "source-over";
    }

    function frame(now) {
      raf = 0;
      if (startedAt === null) startedAt = now;
      pointer.x += (pointer.tx - pointer.x) * 0.05;
      pointer.y += (pointer.ty - pointer.y) * 0.05;
      draw((now - startedAt) / 1000);
      schedule();
    }

    function schedule() {
      if (!raf && visible && !document.hidden && !motionQuery.matches) raf = requestAnimationFrame(frame);
    }

    function renderStill() {
      cancelAnimationFrame(raf);
      raf = 0;
      draw(6);
    }

    function restart() {
      if (motionQuery.matches) renderStill();
      else schedule();
    }

    function onPointer(event) {
      const r = container.getBoundingClientRect();
      pointer.tx = clamp(((event.clientX - r.left) / r.width) * 2 - 1, -1, 1);
      pointer.ty = clamp(((event.clientY - r.top) / r.height) * 2 - 1, -1, 1);
    }

    const resizeObserver = new ResizeObserver(() => {
      resize();
      if (motionQuery.matches) renderStill();
    });
    resizeObserver.observe(canvas);
    const visibility = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
      schedule();
    });
    visibility.observe(canvas);
    // The still frame must follow a light/dark switch; the running loop picks it up by itself.
    const theme = new MutationObserver(() => motionQuery.matches && renderStill());
    theme.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });

    resize();
    restart();
    document.addEventListener("visibilitychange", schedule);
    motionQuery.addEventListener("change", restart);
    window.addEventListener("pointermove", onPointer, { passive: true });

    return () => {
      cancelAnimationFrame(raf);
      resizeObserver.disconnect();
      visibility.disconnect();
      theme.disconnect();
      document.removeEventListener("visibilitychange", schedule);
      motionQuery.removeEventListener("change", restart);
      window.removeEventListener("pointermove", onPointer);
    };
  }, [containerRef]);

  return <canvas ref={canvasRef} aria-hidden="true" className={`pointer-events-none ${className}`} />;
}
